import {
  CLIENT_TYPE_LABELS,
  type ClientDocument,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPES,
  type DocumentType,
  documentChecklist,
  EXTRACTION_SOURCE_LABELS,
  type ExtractionSource,
  formatDateTime,
  RETENTION_YEARS,
} from "@pcj/shared";
import { ArrowLeft, CheckCircle2, ExternalLink, FileText, FolderOpen, ImageIcon, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  Banner,
  Button,
  Card,
  DownloadLink,
  EmptyState,
  Field,
  FileButton,
  inputClass,
  Pill,
  SectionTitle,
} from "../../components/ui";
import { errorMessage } from "../../lib/api";
import {
  DOCUMENT_ACCEPT,
  type ExtractableField,
  extractedFieldLabel,
  type FieldMerge,
  fileProblem,
  isPdf,
  mergeExtracted,
  READABLE_DOCUMENT_TYPES,
} from "../../lib/documents";
import { formatFileSize, plural } from "../../lib/format";
import { documentUrl } from "../../lib/queries";
import { useCompany } from "./CompanyLayout";

/** A client's documents on their own screen: list, viewer, upload and download (route `clients/:clientId/documents`). */
export function ClientDocumentsPage() {
  const { clientId = "" } = useParams();
  const [search, setSearch] = useSearchParams();
  const { clientById, documentsByClient, documentsEnabled, companyId, can, readOnly, actions } = useCompany();
  const client = clientById.get(clientId);
  const documents = documentsByClient.get(clientId) ?? [];
  const checklist = client ? documentChecklist(client.type, documents) : [];
  const firstMissing = checklist.find((item) => item.uploaded === 0)?.type ?? "other";
  const [type, setType] = useState<DocumentType | null>(null);
  const [note, setNote] = useState("");
  const [fileError, setFileError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<{ source: ExtractionSource; merge: FieldMerge } | null>(null);
  const canUpload = can("capture") && !readOnly && documentsEnabled;

  if (!client) {
    return (
      <EmptyState icon={FolderOpen} title="No encontramos ese cliente">
        <Link to={`/companies/${companyId}/clients`} className="underline">
          Volver a clientes
        </Link>
      </EmptyState>
    );
  }

  const selected = documents.find((d) => d.id === search.get("document")) ?? documents.at(-1) ?? null;
  const select = (document: ClientDocument) => setSearch({ document: document.id }, { replace: true });
  const uploadType = type ?? firstMissing;

  function upload(file: File) {
    const problem = fileProblem(file);
    setFileError(problem);
    setSuggestion(null);
    if (problem || !client) return;
    actions.uploadDocument.mutate(
      { clientId: client.id, file, type: uploadType, note: note.trim() || undefined },
      {
        onSuccess: (document) => {
          setNote("");
          setType(null);
          select(document);
          // A constancia in PDF can complete the client's empty fields.
          if (READABLE_DOCUMENT_TYPES.includes(document.type) && isPdf(file)) {
            actions.extractDocument.mutate(file, {
              onSuccess: (extraction) => {
                if (!extraction.source) return;
                const merge = mergeExtracted(client, extraction.fields);
                if (Object.keys(merge.fill).length > 0 || merge.conflicts.length > 0) {
                  setSuggestion({ source: extraction.source, merge });
                }
              },
            });
          }
        },
      },
    );
  }

  function completeDossier(fill: FieldMerge["fill"]) {
    if (!client) return;
    actions.updateClient.mutate({ id: client.id, ...fill }, { onSuccess: () => setSuggestion(null) });
  }

  function remove(document: ClientDocument) {
    if (window.confirm(`¿Eliminar ${document.fileName}? Quedará registrado en la bitácora.`)) {
      actions.deleteDocument.mutate(document.id, {
        onSuccess: () => {
          if (selected?.id === document.id) setSearch({}, { replace: true });
        },
      });
    }
  }

  const fillable = suggestion ? (Object.entries(suggestion.merge.fill) as [ExtractableField, string][]) : [];

  return (
    <div className="space-y-4">
      <Link
        to={`/companies/${companyId}/clients`}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Clientes
      </Link>
      <SectionTitle
        title={`Documentos de ${client.name}`}
        subtitle={`${CLIENT_TYPE_LABELS[client.type]} · ${plural(documents.length, "documento", "documentos")} · se conservan al menos ${RETENTION_YEARS} años`}
      />

      {!documentsEnabled && <Banner tone="info">La carga de documentos no está disponible en este servidor.</Banner>}
      {(actions.uploadDocument.error || actions.deleteDocument.error) && (
        <Banner tone="error">{errorMessage(actions.uploadDocument.error ?? actions.deleteDocument.error)}</Banner>
      )}
      {fileError && <Banner tone="error">{fileError}</Banner>}
      {suggestion && (
        <Banner tone="info" title={`Datos en ${EXTRACTION_SOURCE_LABELS[suggestion.source]}`}>
          {fillable.length > 0 && (
            <>
              <p>El expediente no tiene estos datos y el documento sí:</p>
              <ul className="list-disc pl-5">
                {fillable.map(([field, value]) => (
                  <li key={field}>
                    {extractedFieldLabel(field)}: <strong>{value}</strong>
                  </li>
                ))}
              </ul>
              <Button
                size="sm"
                className="mt-2"
                loading={actions.updateClient.isPending}
                onClick={() => completeDossier(suggestion.merge.fill)}
              >
                Completar el expediente
              </Button>
            </>
          )}
          {suggestion.merge.conflicts.length > 0 && (
            <div className={fillable.length > 0 ? "mt-2" : undefined}>
              <p>El documento indica otros datos; corrígelos en el expediente del cliente si es necesario:</p>
              <ul className="list-disc pl-5">
                {suggestion.merge.conflicts.map((c) => (
                  <li key={c.field}>
                    {extractedFieldLabel(c.field)}: <strong>{c.value}</strong> (expediente: {c.current})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Banner>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="space-y-4">
          {canUpload && (
            <Card className="space-y-3 p-4">
              <p className="text-sm font-medium text-gray-900">Subir un documento</p>
              <Field label="Tipo de documento">
                {(id) => (
                  <select
                    id={id}
                    className={inputClass}
                    value={uploadType}
                    onChange={(e) => setType(e.target.value as DocumentType)}
                  >
                    {DOCUMENT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {DOCUMENT_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="Nota (opcional)" hint="Por ejemplo: «Reverso» o «Vigente hasta 2030».">
                {(id) => (
                  <input
                    id={id}
                    maxLength={200}
                    className={inputClass}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                )}
              </Field>
              <FileButton
                accept={DOCUMENT_ACCEPT}
                label="Archivo del documento"
                variant="primary"
                size="md"
                disabled={actions.uploadDocument.isPending}
                onFile={upload}
              >
                {actions.uploadDocument.isPending ? "Subiendo…" : "Elegir archivo y subir"}
              </FileButton>
              <p className="text-xs text-gray-500">PDF, JPG, PNG o WEBP de hasta 10 MB. Se guarda cifrado.</p>
            </Card>
          )}

          <Card className="p-4">
            <p className="mb-2 text-sm font-medium text-gray-900">Lista del expediente</p>
            <ul className="space-y-1.5 text-sm">
              {checklist.map((item) => (
                <li key={item.type} className="flex items-start justify-between gap-2">
                  <span className="text-gray-700">{item.label}</span>
                  {item.uploaded > 0 ? (
                    <Pill tone="green" icon={CheckCircle2}>
                      {item.uploaded}
                    </Pill>
                  ) : (
                    <Pill>Falta</Pill>
                  )}
                </li>
              ))}
            </ul>
          </Card>

          {documents.length > 0 && (
            <Card className="divide-y divide-gray-100">
              {[...documents].reverse().map((document) => {
                const Icon = document.contentType === "application/pdf" ? FileText : ImageIcon;
                const active = selected?.id === document.id;
                return (
                  <div key={document.id} className={`flex items-start gap-3 p-3 ${active ? "bg-cream" : ""}`}>
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold-500" aria-hidden />
                    <button
                      type="button"
                      onClick={() => select(document)}
                      className="min-w-0 flex-1 text-left"
                      aria-current={active}
                    >
                      <p className="text-sm font-medium text-gray-900">{DOCUMENT_TYPE_LABELS[document.type]}</p>
                      <p className="truncate text-xs text-gray-600">{document.fileName}</p>
                      {document.note && <p className="text-xs text-gray-500">{document.note}</p>}
                      <p className="text-xs text-gray-400">
                        {formatFileSize(document.sizeBytes)} · {formatDateTime(document.createdAt)}
                        {document.uploadedByName ? ` · ${document.uploadedByName}` : ""}
                      </p>
                    </button>
                    {can("delete") && (
                      <Button
                        variant="danger"
                        size="sm"
                        icon={Trash2}
                        onClick={() => remove(document)}
                        aria-label={`Eliminar ${document.fileName}`}
                      />
                    )}
                  </div>
                );
              })}
            </Card>
          )}
        </div>

        {selected ? (
          <DocumentViewer document={selected} companyId={companyId} />
        ) : (
          <EmptyState icon={FolderOpen} title="Este cliente aún no tiene documentos">
            {canUpload ? "Sube su identificación, constancias y comprobantes desde aquí." : null}
          </EmptyState>
        )}
      </div>
    </div>
  );
}

/** The file inside the portal: the browser's PDF viewer, or the image itself. */
function DocumentViewer({ document, companyId }: { document: ClientDocument; companyId: string }) {
  const url = documentUrl(companyId, document.id);
  return (
    <Card className="flex min-h-[60vh] flex-col overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-2">
        <p className="min-w-0 truncate text-sm font-medium text-gray-900">{document.fileName}</p>
        <div className="flex gap-2">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
          >
            <ExternalLink className="h-4 w-4" aria-hidden /> Abrir en otra pestaña
          </a>
          <DownloadLink href={documentUrl(companyId, document.id, true)} size="sm">
            Descargar
          </DownloadLink>
        </div>
      </div>
      {document.contentType === "application/pdf" ? (
        <iframe
          key={document.id}
          src={url}
          title={`Documento: ${document.fileName}`}
          className="min-h-[70vh] w-full flex-1"
        />
      ) : (
        <div className="flex flex-1 items-center justify-center bg-gray-50 p-4">
          <img
            key={document.id}
            src={url}
            alt={`Documento: ${document.fileName}`}
            className="max-h-[70vh] max-w-full"
          />
        </div>
      )}
    </Card>
  );
}
